import { AxiosError } from 'axios';

import { toastFromApiNotification, tryGetMappedNotificationToast } from './backendErrorMessages';

// Enum para tipos de erro conhecidos
export enum ErrorType {
  INTERNET_DISCONNECTED = 'INTERNET_DISCONNECTED',
  CARD_VALIDATION_ERROR = 'CARD_VALIDATION_ERROR',
  CARD_NUMBER_INVALID = 'CARD_NUMBER_INVALID',
  PAYMENT_PROCESSING_ERROR = 'PAYMENT_PROCESSING_ERROR',
  VALIDATION_ERROR_422 = 'VALIDATION_ERROR_422',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  CARD_EXPIRED = 'CARD_EXPIRED',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  INVALID_CARD = 'INVALID_CARD',
  QUOTE_NOT_FOUND = 'QUOTE_NOT_FOUND',
  QUOTE_EXPIRED = 'QUOTE_EXPIRED',
  NOTIFICATION_ERROR = 'NOTIFICATION_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

// Interface para resposta de erro estruturada
export interface ErrorResponse {
  title: string;
  message: string;
  type: 'error' | 'success' | 'warning';
}

// Interface para erros específicos do Pagarme
interface PagarmeErrorResponse {
  message: string;
  errors?: {
    'request.card'?: string[];
    [key: string]: string[] | undefined;
  };
  erros?: {
    'request.card'?: string[];
    [key: string]: string[] | undefined;
  };
}

// Mapeamento de mensagens amigáveis por tipo de erro
const ERROR_MESSAGES: Record<ErrorType, ErrorResponse> = {
  [ErrorType.INTERNET_DISCONNECTED]: {
    title: 'Internet desconectada',
    message: 'Sua conexão com a internet foi perdida. Verifique sua rede Wi-Fi ou dados móveis e tente novamente.',
    type: 'error'
  },

  [ErrorType.CARD_VALIDATION_ERROR]: {
    title: 'Problema no processamento do pagamento',
    message: `Para resolver essa questão, siga as orientações abaixo:
• Certifique-se de digitar o nome exatamente como está impresso no cartão
• Verifique se a data de validade foi inserida corretamente
• Evite o preenchimento automático: insira todas as informações manualmente
• Não utilizar cartões virtuais`,
    type: 'error'
  },

  [ErrorType.CARD_NUMBER_INVALID]: {
    title: 'Número do cartão inválido',
    message: 'O número do cartão informado não é válido. Verifique se você digitou corretamente e tente novamente.',
    type: 'error'
  },

  [ErrorType.PAYMENT_PROCESSING_ERROR]: {
    title: 'Não foi possível processar o pagamento',
    message: 'Houve um problema ao processar seu pagamento. Verifique os dados do cartão e tente novamente.',
    type: 'error'
  },

  [ErrorType.VALIDATION_ERROR_422]: {
    title: 'Dados inválidos',
    message: 'Alguns dados estão incorretos. Verifique as informações e tente novamente.',
    type: 'error'
  },

  [ErrorType.UNAUTHORIZED]: {
    title: 'Acesso não autorizado',
    message: 'Sua sessão expirou. Por favor, recarregue a página e tente novamente.',
    type: 'error'
  },

  [ErrorType.FORBIDDEN]: {
    title: 'Acesso negado',
    message: 'Você não tem permissão para realizar esta operação.',
    type: 'error'
  },

  [ErrorType.NOT_FOUND]: {
    title: 'Dados não encontrados',
    message: 'As informações solicitadas não foram encontradas. Verifique se o link está correto.',
    type: 'error'
  },

  [ErrorType.SERVER_ERROR]: {
    title: 'Erro interno do servidor',
    message: 'Nossos servidores estão temporariamente indisponíveis. Tente novamente em alguns minutos.',
    type: 'error'
  },

  [ErrorType.TIMEOUT_ERROR]: {
    title: 'Tempo limite excedido',
    message: 'A operação demorou mais que o esperado. Verifique sua conexão e tente novamente.',
    type: 'error'
  },

  [ErrorType.CARD_EXPIRED]: {
    title: 'Cartão vencido',
    message: 'Seu cartão está vencido. Utilize um cartão válido para continuar.',
    type: 'error'
  },

  [ErrorType.INSUFFICIENT_FUNDS]: {
    title: 'Saldo insuficiente',
    message: 'Não há saldo suficiente no cartão para esta transação. Verifique seu limite ou utilize outro cartão.',
    type: 'error'
  },

  [ErrorType.INVALID_CARD]: {
    title: 'Cartão inválido',
    message: 'Os dados do cartão estão incorretos. Verifique o número, CVV e data de validade.',
    type: 'error'
  },

  [ErrorType.QUOTE_NOT_FOUND]: {
    title: 'Cotação não encontrada',
    message: 'A cotação não foi encontrada ou já expirou. Por favor, faça uma nova cotação.',
    type: 'error'
  },

  [ErrorType.QUOTE_EXPIRED]: {
    title: 'Cotação expirada',
    message: 'Esta cotação já expirou. Por favor, faça uma nova cotação para continuar.',
    type: 'error'
  },
  [ErrorType.NOTIFICATION_ERROR]: {
    title: 'Erro de processamento',
    message: 'Ocorreu um erro durante o processamento. Verifique as informações e tente novamente.',
    type: 'error'
  },
  [ErrorType.UNKNOWN_ERROR]: {
    title: 'Erro inesperado',
    message: 'Ocorreu um erro inesperado. Se o problema persistir, entre em contato com nosso atendimento.',
    type: 'error'
  }
};

// Função para identificar o tipo de erro baseado no erro recebido
export function identifyErrorType(error: any): ErrorType {
  // Se for um AxiosError
  if (error?.isAxiosError) {
    const axiosError = error as AxiosError<any>;
    const status = axiosError.response?.status;
    const message = axiosError.message;
    const responseData = axiosError.response?.data;

    // Erro específico de internet desconectada
    if (message?.includes('ERR_INTERNET_DISCONNECTED')) {
      return ErrorType.INTERNET_DISCONNECTED;
    }

    // Erro de timeout
    if (message?.includes('timeout')) {
      return ErrorType.TIMEOUT_ERROR;
    }

    // Baseado no status HTTP
    switch (status) {
      case 401:
        return ErrorType.UNAUTHORIZED;
      case 403:
        return ErrorType.FORBIDDEN;
      case 404:
        return ErrorType.NOT_FOUND;
      case 422:
        // Verificar mensagens específicas de erro de cartão
        const cardErrors = responseData?.errors?.['request.card'] || responseData?.erros?.['request.card'];

        if (cardErrors && Array.isArray(cardErrors)) {
          // Verificar se há a mensagem específica de número inválido
          const hasInvalidNumberError = cardErrors.some((error) =>
            error.includes('The number field is not a valid card number')
          );

          if (hasInvalidNumberError) {
            return ErrorType.CARD_NUMBER_INVALID;
          }
        }

        // Se tem erro específico de cartão mas não é número inválido
        if (cardErrors) {
          return ErrorType.CARD_VALIDATION_ERROR;
        }

        return ErrorType.VALIDATION_ERROR_422;
      case 500:
      case 502:
      case 503:
      case 504:
        return ErrorType.SERVER_ERROR;
      default:
        // Verificar se há notificações no response (independente do data)
        if (
          responseData?.notifications &&
          Array.isArray(responseData.notifications) &&
          responseData.notifications.length > 0
        ) {
          return ErrorType.NOTIFICATION_ERROR;
        }

        // Verificar mensagens específicas conhecidas
        if (responseData?.message) {
          const errorMessage = responseData.message.toLowerCase();

          if (errorMessage.includes('cartão vencido') || errorMessage.includes('card expired')) {
            return ErrorType.CARD_EXPIRED;
          }

          if (errorMessage.includes('saldo insuficiente') || errorMessage.includes('insufficient funds')) {
            return ErrorType.INSUFFICIENT_FUNDS;
          }

          if (errorMessage.includes('cartão inválido') || errorMessage.includes('invalid card')) {
            return ErrorType.INVALID_CARD;
          }

          if (errorMessage.includes('cotação não encontrada') || errorMessage.includes('quote not found')) {
            return ErrorType.QUOTE_NOT_FOUND;
          }

          if (errorMessage.includes('cotação expirada') || errorMessage.includes('quote expired')) {
            return ErrorType.QUOTE_EXPIRED;
          }
        }

        return ErrorType.PAYMENT_PROCESSING_ERROR;
    }
  }

  // Erros não-axios
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('err_internet_disconnected')) {
      return ErrorType.INTERNET_DISCONNECTED;
    }

    if (message.includes('timeout')) {
      return ErrorType.TIMEOUT_ERROR;
    }
  }

  return ErrorType.UNKNOWN_ERROR;
}

// Função principal para obter mensagem amigável de erro
export function getErrorMessage(error: any, customMessage?: string): ErrorResponse {
  const errorType = identifyErrorType(error);
  const baseResponse = ERROR_MESSAGES[errorType];

  const mappedNotificationToast = tryGetMappedNotificationToast(error);
  if (mappedNotificationToast) {
    return mappedNotificationToast;
  }

  // Se uma mensagem customizada foi fornecida, use ela mantendo o título
  if (customMessage) {
    return {
      ...baseResponse,
      message: customMessage
    };
  }

  // Verificar se é um erro com notificações e usar a mensagem específica
  if (error?.isAxiosError) {
    const responseData = error.response?.data;
    if (responseData && 'notifications' in responseData && Array.isArray(responseData.notifications)) {
      const notifications = responseData.notifications;

      // Primeira notificação: respeita `key` com mapeamento em backendErrorMessages, senão usa `message` da API
      if (notifications.length > 0) {
        const first = notifications[0] as { key?: string; message?: string };
        if (first && typeof first === 'object') {
          return toastFromApiNotification({
            key: typeof first.key === 'string' ? first.key : undefined,
            message: typeof first.message === 'string' ? first.message : undefined
          });
        }
      }
    }
  }

  return baseResponse;
}

// Função para obter mensagem personalizada para erros 422 específicos
export function get422ErrorMessage(error: AxiosError<PagarmeErrorResponse>): ErrorResponse {
  const responseData = error.response?.data;
  const cardErrors = responseData?.errors?.['request.card'] || responseData?.erros?.['request.card'];

  if (cardErrors && Array.isArray(cardErrors)) {
    // Verificar se há a mensagem específica de número inválido
    const hasInvalidNumberError = cardErrors.some((error) =>
      error.includes('The number field is not a valid card number')
    );

    if (hasInvalidNumberError) {
      return ERROR_MESSAGES[ErrorType.CARD_NUMBER_INVALID];
    }

    // Outros erros de cartão
    return ERROR_MESSAGES[ErrorType.CARD_VALIDATION_ERROR];
  }

  // Se tem uma mensagem específica na resposta, use ela
  if (responseData?.message) {
    return {
      title: 'Problema nos dados informados',
      message: responseData.message,
      type: 'error'
    };
  }

  return ERROR_MESSAGES[ErrorType.VALIDATION_ERROR_422];
}

// Função auxiliar para logging de erros com contexto
export function logErrorWithContext(error: any, context: string, additionalData?: Record<string, any>) {
  const errorType = identifyErrorType(error);
  const errorInfo = {
    errorType,
    context,
    timestamp: new Date().toISOString(),
    ...additionalData
  };

  console.error(`[${context}] Erro identificado como: ${errorType}`, {
    error,
    ...errorInfo
  });

  return errorInfo;
}
